import { Check, FileText, ShieldCheck } from "lucide-react";
import { Eyebrow, Reveal, BookCallButton } from "./utils";

const includes = [
  "Revenue leak analysis",
  "Lead response and follow-up review",
  "Quote and estimate recovery opportunities",
  "Past-customer reactivation review",
  "CRM and pipeline visibility review",
  "Review, referral, retention, and upsell opportunities",
  "Priority recommendations",
  "30-, 60-, and 90-day action plan",
  "Recommended Apex Flow package and explanation",
];

function DocMockup() {
  return (
    <div className="relative [perspective:1500px]">
      <div
        className="relative mx-auto w-full max-w-[420px] preserve-3d"
        style={{ transform: "rotateY(-18deg) rotateX(6deg)" }}
      >
        {/* back sheet */}
        <div className="absolute -right-5 top-6 h-full w-full rounded-2xl bg-brand-gray-soft/70 shadow-luxe" />
        {/* main document */}
        <div className="relative rounded-2xl bg-white border border-brand-gray-soft shadow-[0_40px_90px_-30px_rgba(17,17,17,0.35)] p-8">
          <div className="flex items-center justify-between">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gold/12">
              <FileText className="h-5 w-5 text-brand-gold" strokeWidth={1.6} />
            </span>
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-gold/40 bg-brand-gold/8">
              <ShieldCheck className="h-5 w-5 text-brand-gold-deep" strokeWidth={1.6} />
            </span>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-gold">Revenue Opportunity Roadmap</p>
          <div className="mt-3 h-3 w-3/4 rounded-full bg-brand-black/85" />
          <div className="mt-2 h-3 w-1/2 rounded-full bg-brand-black/15" />

          <div className="mt-7 space-y-3">
            {[80, 64, 92, 50].map((w, i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-1.5 rounded-full bg-brand-gray-soft" style={{ width: `${w}%` }} />
                <div className="h-1.5 rounded-full bg-brand-gray-soft/60" style={{ width: `${w - 24}%` }} />
              </div>
            ))}
          </div>

          <div className="mt-7 grid grid-cols-3 gap-3">
            {["30", "60", "90"].map((d) => (
              <div key={d} className="rounded-xl border border-brand-gray-soft bg-brand-gray-light p-3 text-center">
                <p className="font-serif text-2xl text-brand-black leading-none">{d}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-wider text-brand-black/45">Day Plan</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RevenueRoadmap() {
  return (
    <section data-testid="revenue-roadmap-section" id="roadmap" className="relative overflow-hidden bg-white py-24 md:py-32">
      <div className="pointer-events-none absolute -left-20 top-1/3 h-[400px] w-[400px] rounded-full bg-brand-gold/8 blur-[120px]" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal><Eyebrow>Your 90-Day Growth Blueprint</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-brand-black leading-[1.08]">
              See Where Revenue Is Leaking—And <span className="gold-text">What to Fix First.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-brand-black/65">
              The Apex Flow Revenue Opportunity Roadmap gives business owners a practical, human-reviewed view of the
              strongest growth opportunities inside their business.
            </p>
          </Reveal>

          <Reveal delay={2}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {includes.map((it) => (
                <li key={it} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" strokeWidth={2} />
                  <span className="font-sans text-sm leading-snug text-brand-black/75">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={3}>
            <div className="mt-9 rounded-2xl bg-brand-black p-7 md:p-8 shadow-luxe">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-gold">Revenue Opportunity Roadmap</p>
                  <p className="mt-2 font-serif text-5xl text-white leading-none">$750</p>
                </div>
                <BookCallButton id="roadmap-book-call" label="Book a Free Call" variant="gold" />
              </div>
              <p className="mt-5 font-sans text-sm leading-relaxed text-white/55">
                If you enroll in an Apex Flow monthly package within 30 days, your $750 Roadmap investment is credited
                toward your first month.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={1}>
          <DocMockup />
        </Reveal>
      </div>
    </section>
  );
}
